/**
 * Record actions your users perform
 * @param {string} eventAction - The name of the event you’re tracking.
 * @param {object} properties - Optional. A dictionary of properties for the event.
 * @param {object} options - Optional. A dictionary of options.
 */
function trackEvent(eventDefinition) {
  return (action, previousState, nextState) => {
    const event = eventDefinition(action, previousState, nextState);
    return {
      hitType: 'event',
      eventAction: event.name,
      properties: event.properties,
      options: event.options,
    };
  };
}

/**
 * Link your users and their actions, to a recognizable userId and traits.
 * @param {string} userId - The database ID for the user.
 * @param {object} traits - A dictionary of traits you know about the user, like email or name.
 * @param {object} options - A dictionary of options.
 */
function identifyUser(eventDefinition) {
  return (action, previousState, nextState) => {
    const event = eventDefinition(action, previousState, nextState);
    return {
      hitType: 'identify',
      userId: event.userId,
      traits: event.traits,
      options: event.options,
    };
  };
}

/**
 * Record page views on your website, along with optional extra information about the page viewed
 * by the user.
 * @param {string} page - The page type.
 * @param {object} name - The name of the page.
 * @param {object} properties - A dictionary of properties of the page
 * @param {object} options - A dictionary of options.
 */
function trackPageView(eventDefinition) {
  return (action, previousState, nextState) => {
    const event = eventDefinition(action, previousState, nextState);
    return {
      hitType: 'pageview',
      page: event.category,
      name: event.name,
      properties: event.properties,
      options: event.options,
    };
  };
}

/**
 * Record page views on your website, along with optional extra information about the page viewed
 * by the user.
 * @param {string} groupId - The Group ID to associate with the current user.
 * @param {object} traits - A dictionary of traits for the group.
 * @param {object} options - A dictionary of options.
 */
function setGroup(eventDefinition) {
  return (action, previousState, nextState) => {
    const event = eventDefinition(action, previousState, nextState);
    return {
      hitType: 'group',
      groupId: event.groupId,
      traits: event.traits,
      options: event.options,
    };
  };
}

/**
 * Combines two unassociated user identities.
 * @param {string} userId - The new user ID you want to associate with the user.
 * @param {object} previousId - The previous ID that the user was recognized by.
 * @param {object} options - A dictionary of options.
 */
function setAlias(eventDefinition) {
  return (action, previousState, nextState) => {
    const event = eventDefinition(action, previousState, nextState);
    return {
      hitType: 'alias',
      userId: event.userId,
      previousId: event.previousId,
      options: event.options,
    };
  };
}

/* Calling reset resets the id, including anonymousId, and clears traits for the currently identified user and group. */
function reset() {
  return () => ({
    hitType: 'reset',
  });
}

export { trackEvent, identifyUser, trackPageView, setGroup, setAlias, reset };
